/**
 * The address the identity provider will send the user back to must be one it accepts.
 *
 * The login sends the whole current address as `redirect_uri`, fragment included, and the identity
 * provider rejects one that contains `{` or `}`: "Invalid parameter: redirect_uri", a page the user
 * cannot leave. The API reference of the Developers page (Scalar) keeps its position in the fragment,
 * and every operation with a path parameter has braces in it
 * (`#tag/async-jobs/GET/v1/document/jobs/{jobId}`). Reloading there, or opening that link from an
 * e-mail, ended on that error page. Found in production on 2026-09-20.
 *
 * So, before the login starts, a fragment with braces is dropped. The page and its `?tab=` survive;
 * the exact position inside the reference does not.
 *
 * The answer of the identity provider also arrives in the fragment (`#state=…&code=…`): that one
 * never has braces, and it is left alone in any case.
 */
export function loginSafeFragment(fragment: string): string {
  if (!fragment) return fragment;
  if (/(^|[#&])(state|code|error)=/.test(fragment)) return fragment;
  return /[{}]|%7B|%7D/i.test(fragment) ? '' : fragment;
}

/** Applies {@link loginSafeFragment} to the address bar. Call it before the SSO client initialises. */
export function makeAddressLoginSafe(): void {
  const safe = loginSafeFragment(window.location.hash);
  if (safe === window.location.hash) return;
  window.history.replaceState(window.history.state, '', window.location.pathname + window.location.search + safe);
}
